import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router';
import { BRAND } from '@teachpartner/config/brand';
import { describeError, httpApi, type HostAdminApi } from './api/client';
import { AppServicesProvider } from './app-context';
import { CALLBACK_PATH, type AuthService, type HostAdminUser } from './auth/auth';
import { ErrorBanner } from './components/badges';
import { Layout } from './components/Layout';
import { AuditPage } from './pages/AuditPage';
import { PushStatsPage } from './pages/PushStatsPage';
import { TenantDetailPage } from './pages/TenantDetailPage';
import { TenantsPage } from './pages/TenantsPage';
import { UpdatesPage } from './pages/UpdatesPage';

interface AppProps {
  readonly auth: AuthService;
  readonly apiBase: string;
  /** Tests inject a fake API and a memory router. */
  readonly api?: HostAdminApi;
  readonly router?: (children: React.ReactNode) => React.ReactNode;
}

function SignIn({ auth, onUser }: { auth: AuthService; onUser(u: HostAdminUser | null): void }) {
  const [error, setError] = useState<string | null>(null);
  // OIDC navigates away to Keycloak; mock mode signs in locally and resolves a user.
  const start = () =>
    auth
      .signIn()
      .then(() => auth.currentUser())
      .then(onUser, (e: unknown) => setError(describeError(e)));
  return (
    <main className="signin">
      <img
        className="brand-icon brand-icon-large"
        src="/favicon.svg"
        alt=""
        width={56}
        height={56}
      />
      <h1>{BRAND.displayName} Host Admin</h1>
      <p>Mantle staff only. Sign in with your Host Admin account.</p>
      <ErrorBanner message={error} />
      <button type="button" onClick={() => void start()}>
        Sign in
      </button>
    </main>
  );
}

function Callback({ auth, onUser }: { auth: AuthService; onUser(u: HostAdminUser): void }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    auth.completeSignIn().then(
      (user) => {
        onUser(user);
        void navigate('/', { replace: true });
      },
      (e: unknown) => setError(describeError(e)),
    );
  }, [auth, navigate, onUser]);
  return error ? <ErrorBanner message={error} /> : <p>Signing in…</p>;
}

export function App({ auth, apiBase, api, router }: AppProps) {
  const [user, setUser] = useState<HostAdminUser | null | undefined>(undefined);
  const client = useMemo(
    () => api ?? httpApi({ baseUrl: apiBase, getToken: () => auth.accessToken() }),
    [api, apiBase, auth],
  );

  useEffect(() => {
    auth.currentUser().then(setUser, () => setUser(null));
  }, [auth]);

  const wrap = router ?? ((children) => <BrowserRouter>{children}</BrowserRouter>);
  if (user === undefined) return <p>Loading…</p>;

  return wrap(
    <Routes>
      <Route path={CALLBACK_PATH} element={<Callback auth={auth} onUser={setUser} />} />
      {user ? (
        <Route
          element={
            <AppServicesProvider value={{ api: client, auth, user }}>
              <Layout />
            </AppServicesProvider>
          }
        >
          <Route index element={<TenantsPage />} />
          <Route path="tenants/:code" element={<TenantDetailPage />} />
          <Route path="push" element={<PushStatsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="updates" element={<UpdatesPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      ) : (
        <Route path="*" element={<SignIn auth={auth} onUser={setUser} />} />
      )}
    </Routes>,
  );
}
